import PropTypes from 'prop-types';
import { BsTelephone, BsDash } from 'react-icons/bs';

import IconButton from '#globalComponents/IconButton/IconButton';

import { getStateColors } from './callOverlayUtils';
import CallPartyInfo from './CallPartyInfo';
import PulsingDot from './PulsingDot';

const ExpandedCallOverlay = ({
	bottomClass,
	label,
	displayNumber,
	isRinging,
	isActive,
	isEnded,
	durationText,
	DirectionIcon,
	direction,
	lookup,
	onSelectCompany,
	widgetVisible,
	onToggleWidget,
	onMinimize,
}) => {
	const colors = getStateColors(isRinging, isActive);

	return (
		<div
			className={`fixed ${bottomClass} right-4 z-40 w-[360px] bg-white rounded-card border border-bgBase-500 shadow-elevated overflow-hidden`}
		>
			<div className={`px-4 py-3 flex items-center justify-between ${colors.headerBg}`}>
				<div className="flex items-center gap-2">
					{(isRinging || isActive) && <PulsingDot colors={colors} showPulse />}
					<span className={`text-xs font-semibold ${colors.headerText}`}>{label}</span>
				</div>
				<div className="flex items-center gap-1">
					<IconButton icon={BsDash} label="Minimieren" onClick={onMinimize} className="h-6 w-6" />
				</div>
			</div>

			<div className="px-4 py-4 space-y-3">
				<div className="flex items-center gap-3">
					<div
						className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.bg} ${colors.text}`}
					>
						<DirectionIcon className="w-4 h-4" />
					</div>
					<div className="min-w-0 flex-1">
						{displayNumber ? (
							<p className="text-sm font-semibold text-text font-mono">{displayNumber}</p>
						) : (
							<p className="text-sm text-bgBase-800">Unbekannte Nummer</p>
						)}
						<p className="text-[10px] text-bgBase-800 mt-0.5">
							{direction === 'inbound' ? 'Eingehend' : 'Ausgehend'}
						</p>
					</div>
				</div>

				{/* Caller identification — matched CRM contacts/companies (inbound only) */}
				{onSelectCompany && <CallPartyInfo lookup={lookup} onSelectCompany={onSelectCompany} />}

				{/* Ringing — no duration yet, just the ring indicator */}
				{isRinging && (
					<div className="flex items-center gap-2 px-3 py-2 bg-bgBase-300 rounded-card">
						<BsTelephone className="w-3 h-3 text-bgBase-800" />
						<span className="text-xs text-text">{durationText}</span>
					</div>
				)}

				{/* Active — live talk timer */}
				{isActive && (
					<div className="flex items-center gap-2 px-3 py-2 bg-bgBase-300 rounded-card">
						<BsTelephone className="w-3 h-3 text-bgBase-800" />
						<span className="text-xs text-text">Dauer:</span>
						<span className="text-xs font-semibold text-text tabular-nums">{durationText}</span>
					</div>
				)}

				{/* Ended — frozen SDK duration */}
				{isEnded && (
					<div className="flex items-center gap-2 px-3 py-2 bg-bgBase-300 rounded-card">
						<span className="text-xs text-bgBase-800">Anruf beendet &middot; {durationText}</span>
					</div>
				)}

				<button
					onClick={onToggleWidget}
					className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-primary-700 bg-primary-100 hover:bg-primary-200 rounded-control transition-colors"
				>
					<BsTelephone className="w-3 h-3" />
					{widgetVisible ? 'Aircall-Widget ausblenden' : 'Aircall-Widget einblenden'}
				</button>
			</div>
		</div>
	);
};

ExpandedCallOverlay.propTypes = {
	bottomClass: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	displayNumber: PropTypes.string,
	isRinging: PropTypes.bool.isRequired,
	isActive: PropTypes.bool.isRequired,
	isEnded: PropTypes.bool.isRequired,
	durationText: PropTypes.string,
	DirectionIcon: PropTypes.elementType.isRequired,
	direction: PropTypes.string,
	lookup: PropTypes.shape({ contacts: PropTypes.array, companies: PropTypes.array }),
	onSelectCompany: PropTypes.func,
	widgetVisible: PropTypes.bool.isRequired,
	onToggleWidget: PropTypes.func.isRequired,
	onMinimize: PropTypes.func.isRequired,
};

export default ExpandedCallOverlay;
